/**
 * Shared Vervit add-to-cart — handles success, max-quantity (422), and cart UI sync
 * without opening the cart drawer or showing false "Try again" states.
 */
(function () {
  function getCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getErrorMessage(data) {
    return String((data && (data.description || data.message)) || '').toLowerCase();
  }

  // Shopify's stock-limit rejections, e.g. "All 1 Ring are in your cart." or
  // "You can only add 3 Ring to the cart." Any OTHER 422 (sold out, invalid
  // variant, missing required property...) is a genuine error and must not be
  // treated as "already at max quantity".
  const STOCK_LIMIT_PATTERN =
    /\bin your cart\b|\bonly add\s+\d+|\bmaximum\b|\bcan only (?:add|buy|purchase)\b|\bnot enough (?:stock|inventory)\b/;

  function isMaxQuantityResponse(data) {
    if (!data || typeof data !== 'object') return false;
    return STOCK_LIMIT_PATTERN.test(getErrorMessage(data));
  }

  function getVariantQuantityInCart(variantId) {
    const fetchFn =
      window.__vrvTrueNativeFetch || window.__vrvNativeFetch || (window.fetch ? window.fetch.bind(window) : null);
    if (!fetchFn) return Promise.reject(new Error('fetch unavailable'));
    return fetchFn('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('cart unavailable'))))
      .then((cart) =>
        ((cart && cart.items) || []).reduce(
          (sum, item) =>
            String(item.variant_id || item.id) === String(variantId) ? sum + (Number(item.quantity) || 0) : sum,
          0
        )
      );
  }

  function buildStockLimitMessage(available, inCart) {
    if (inCart >= available) {
      return available === 1
        ? 'Only 1 item is available in stock, and it has already been added to your bag.'
        : 'Only ' + available + ' items are available in stock, and all of them have already been added to your bag.';
    }
    if (inCart > 0) {
      const remaining = available - inCart;
      return (
        'Only ' + available + ' items are available in stock, and ' + inCart + ' ' +
        (inCart === 1 ? 'is' : 'are') + ' already in your bag. You can add ' + remaining + ' more.'
      );
    }
    return 'Only ' + available + (available === 1 ? ' item is' : ' items are') + ' available in stock. Please reduce the quantity.';
  }

  function friendlyErrorText(data) {
    // Shopify's `description` is the human-readable reason; `message` is
    // usually the technical label ("Cart Error").
    const description = data && typeof data.description === 'string' ? data.description.trim() : '';
    if (description) return description;
    const message = data && typeof data.message === 'string' ? data.message.trim() : '';
    if (message && !/^cart error$/i.test(message)) return message;
    return "We couldn't add this item to your bag. Please try again.";
  }

  /**
   * Classify a failed cart add and build the message to show the customer.
   * ctx: { variantId, available } -- `available` is the variant's tracked
   * inventory when the page knows it (optional).
   * Resolves { stockLimit: boolean, message: string }. Genuine (non-stock)
   * failures keep Shopify's own reason and are never reported as stock errors.
   */
  function describeAddFailure(data, ctx) {
    ctx = ctx || {};
    const text = data && typeof data === 'object' ? String(data.description || data.message || '') : '';
    const known = Number(ctx.available);
    const hasKnown = Number.isFinite(known) && known > 0;

    if (isMaxQuantityResponse(data)) {
      // "All N ... are in your cart" => the cart already holds every unit.
      const allMatch = /\ball\s+(\d+)\b/i.exec(text);
      if (allMatch) {
        const available = parseInt(allMatch[1], 10);
        return Promise.resolve({ stockLimit: true, message: buildStockLimitMessage(available, available) });
      }
      if (hasKnown && ctx.variantId) {
        return getVariantQuantityInCart(ctx.variantId)
          .then((inCart) => ({ stockLimit: true, message: buildStockLimitMessage(known, inCart) }))
          .catch(() => ({ stockLimit: true, message: friendlyErrorText(data) }));
      }
      return Promise.resolve({ stockLimit: true, message: friendlyErrorText(data) });
    }

    // No readable reason came back (empty/non-JSON response). If the page knows
    // the variant's stock, the cart tells us whether the limit is what blocked it.
    if (!text && hasKnown && ctx.variantId) {
      return getVariantQuantityInCart(ctx.variantId)
        .then((inCart) =>
          inCart >= known
            ? { stockLimit: true, message: buildStockLimitMessage(known, inCart) }
            : { stockLimit: false, message: friendlyErrorText(data) }
        )
        .catch(() => ({ stockLimit: false, message: friendlyErrorText(data) }));
    }

    return Promise.resolve({ stockLimit: false, message: friendlyErrorText(data) });
  }

  let cartMessageTimer = null;
  function showCartMessage(message) {
    if (!message) return;
    if (!document.body) {
      window.alert(message);
      return;
    }
    let el = document.getElementById('vrv-cart-message');
    if (!el) {
      el = document.createElement('div');
      el.id = 'vrv-cart-message';
      el.setAttribute('role', 'alert');
      el.setAttribute('aria-live', 'assertive');
      el.style.cssText =
        'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:2147483000;' +
        'max-width:min(92vw,460px);padding:12px 18px;border-radius:8px;background:#1a1a1a;color:#fff;' +
        'font-size:14px;line-height:1.4;text-align:center;box-shadow:0 6px 24px rgba(0,0,0,.25);';
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
    clearTimeout(cartMessageTimer);
    cartMessageTimer = setTimeout(() => {
      el.hidden = true;
    }, 6000);
  }

  function isCartAddError(data, ok) {
    if (isMaxQuantityResponse(data)) return false;
    if (!data || typeof data !== 'object') return !ok;
    const status = Number(data.status);
    if (!Number.isNaN(status) && status >= 400) return true;
    if (!ok && !data.id && !data.variant_id && !data.key && !(Array.isArray(data.items) && data.items.length)) {
      return Boolean(data.description || data.message);
    }
    return false;
  }

  function isCartAddSuccess(data, ok) {
    if (isMaxQuantityResponse(data)) return false;
    if (isCartAddError(data, ok)) return false;
    if (ok) return true;
    return Boolean(
      data &&
        (data.id ||
          data.variant_id ||
          data.key ||
          (Array.isArray(data.items) && data.items.length) ||
          data.sections)
    );
  }

  function appendSections(formData, cartEl) {
    let sectionIds = [];
    if (typeof window.vrvGetCartAddSections === 'function') {
      sectionIds = window.vrvGetCartAddSections();
    } else if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      sectionIds = cartEl.getSectionsToRender().map((s) => s.id);
    }
    if (!sectionIds.length) return;
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
    if (cartEl && typeof cartEl.setActiveElement === 'function') {
      cartEl.setActiveElement(document.activeElement);
    }
  }

  function openCartDrawerAfterAdd(trigger) {
    const wishlistDrawer = document.getElementById('wishlist-drawer');
    if (wishlistDrawer && wishlistDrawer.classList.contains('active')) {
      wishlistDrawer.classList.remove('active');
      wishlistDrawer.setAttribute('aria-hidden', 'true');
      const overlay = document.getElementById('wishlist-overlay');
      overlay?.classList.remove('active');
      overlay?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
    }

    if (window.vrvSpinWheel && !window.vrvSpinWheel.hasPlayed()) {
      var didOpen = window.vrvSpinWheel.open();
      if (didOpen) return;
    }

    if (document.getElementById('VrvCartPage')) return;
    if (typeof window.vrvOpenCartDrawer === 'function') {
      window.vrvOpenCartDrawer(trigger);
      return;
    }
    const el = getCartElement();
    if (el && typeof el.open === 'function') {
      el.open(trigger || document.activeElement);
    }
  }

  function syncCartUi(cartEl, data, trigger, options) {
    const opts = Object.assign({ openDrawer: true }, options || {});
    const el = cartEl || getCartElement();
    if (el && typeof el.clearPrefetchCache === 'function') {
      el.clearPrefetchCache();
    }
    if (el) {
      el.classList.remove('is-empty');
    }

    if (typeof window.vrvRefreshCartFromAdd === 'function') {
      return window.vrvRefreshCartFromAdd(data, {
        trigger: trigger,
        openDrawer: opts.openDrawer !== false,
      }).catch((e) => {
        console.warn('vrvRefreshCartFromAdd failed', e);
        if (opts.openDrawer !== false) {
          openCartDrawerAfterAdd(trigger);
        }
      });
    }

    if (!el) {
      if (opts.openDrawer !== false) openCartDrawerAfterAdd(trigger);
      return Promise.resolve();
    }

    try {
      if (data && data.sections && typeof el.renderContents === 'function') {
        return Promise.resolve(window.vrvBeforeCartRender ? window.vrvBeforeCartRender(data) : data)
          .then((finalData) => {
            el.renderContents(finalData);
            if (opts.openDrawer !== false) openCartDrawerAfterAdd(trigger);
          })
          .catch(() => {
            el.renderContents(data);
            if (opts.openDrawer !== false) openCartDrawerAfterAdd(trigger);
          });
      }

      if (typeof el.refreshDrawer === 'function') {
        return el.refreshDrawer(true).then(() => {
          if (opts.openDrawer !== false) openCartDrawerAfterAdd(trigger);
        });
      }

      if (opts.openDrawer !== false) openCartDrawerAfterAdd(trigger);
    } catch (e) {
      console.warn('Cart UI sync failed', e);
      if (opts.openDrawer !== false) openCartDrawerAfterAdd(trigger);
    }
    return Promise.resolve();
  }

  function publishCartUpdate(source, data, variantId) {
    // Same fix as cart.js's updateQuantity: invalidate cart-drawer-extras.js's
    // short-lived /cart.js dedupe cache the moment a mutation is confirmed,
    // so nothing downstream can render a pre-add snapshot.
    if (typeof window.vrvSetCurrentCart === 'function' && data && Array.isArray(data.items)) {
      window.vrvSetCurrentCart(data);
    } else if (typeof window.vrvInvalidateCartJsCache === 'function') {
      window.vrvInvalidateCartJsCache();
    }
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: source || 'vrv-add-to-cart',
          productVariantId: variantId,
          cartData: data,
        });
      }
    } catch (e) {
      console.warn('cartUpdate publish failed', e);
    }

    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (_) {
      /* ignore */
    }
  }

  function resetButton(button, text, delay) {
    setTimeout(() => {
      button.textContent = text;
      button.disabled = false;
    }, delay);
  }

  function getCartAddJsUrl() {
    const raw = (window.routes && window.routes.cart_add_url) || '/cart/add';
    if (/\.js(?:\?|$)/.test(String(raw))) return raw;
    return String(raw).replace(/\/$/, '') + '.js';
  }

  function fetchCartAdd(formData) {
    const addUrl = getCartAddJsUrl();
    return fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    }).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((data) => {
          if (!response.ok) {
            console.warn('[VRV cart/add] EXACT REASON:', {
              httpStatus: response.status,
              shopifyMessage: data.description || data.message || null,
              shopifyErrors: data.errors || data.items || null,
              statusField: data.status || null,
              why422:
                response.status === 422
                  ? 'Shopify rejected the add — usually sold out, max quantity in cart, invalid variant id, or missing required line-item property.'
                  : null,
              variantId: formData && typeof formData.get === 'function' ? formData.get('id') : null,
              quantity: formData && typeof formData.get === 'function' ? formData.get('quantity') : null,
            });
          }
          return {
            ok: response.ok,
            data: data && typeof data === 'object' ? data : {},
          };
        })
    );
  }

  function getCartAddSectionIds(cartEl) {
    if (typeof window.vrvGetCartAddSections === 'function') {
      return window.vrvGetCartAddSections();
    }
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      return cartEl.getSectionsToRender().map((section) => section.id);
    }
    return [];
  }

  function fetchCartAddItems(items, cartEl) {
    const addUrl = getCartAddJsUrl();
    const normalizedItems = (items || []).map((item) => {
      const line = {
        id: Number(item.id),
        quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
      };
      if (item.properties && typeof item.properties === 'object' && Object.keys(item.properties).length) {
        line.properties = item.properties;
      }
      if (item.selling_plan) line.selling_plan = Number(item.selling_plan);
      return line;
    });
    const payload = { items: normalizedItems };
    const sectionIds = getCartAddSectionIds(cartEl);
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
      if (cartEl && typeof cartEl.setActiveElement === 'function') {
        cartEl.setActiveElement(document.activeElement);
      }
    }

    return fetch(addUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    }).then((response) =>
      response
        .json()
        .catch(() => ({}))
        .then((data) => ({
          ok: response.ok,
          // Exposed so callers can recognize a rate-limit (429) response --
          // Cloudflare returns an HTML block page for those, not JSON, so
          // `data` ends up empty and has no description/message to check.
          status: response.status,
          data: data && typeof data === 'object' ? data : {},
        }))
    );
  }

  window.vrvAddToCart = function (variantId, button, source, quantity) {
    if (!variantId || !button) return Promise.resolve();

    const originalText = (button.textContent || button.innerText || 'Add to Cart').trim();
    const cartEl = getCartElement();
    const qty = Math.min(99, Math.max(1, parseInt(quantity, 10) || 1));

    button.textContent = 'Adding...';
    button.disabled = true;
    button.classList.add('loading', 'is-loading');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', qty);
    appendSections(formData, cartEl);

    return fetchCartAdd(formData)
      .then(({ ok, data }) => {
        if (isMaxQuantityResponse(data)) {
          button.classList.remove('loading', 'is-loading');
          button.textContent = 'In cart';
          // Tell the customer why nothing was added, without waiting on the cart refresh.
          describeAddFailure(data, { variantId: variantId }).then((failure) => showCartMessage(failure.message));
          return syncCartUi(cartEl, data, button, { openDrawer: false }).then(() => {
            publishCartUpdate(source, data, variantId);
            resetButton(button, originalText, 1500);
            return data;
          });
        }

        if (!isCartAddSuccess(data, ok)) {
          return describeAddFailure(data, { variantId: variantId }).then((failure) => {
            showCartMessage(failure.message);
            const error = new Error(failure.message);
            error.vrvMessageShown = true;
            throw error;
          });
        }

        button.textContent = 'Adding...';
        return syncCartUi(cartEl, data, button, { openDrawer: true }).then(() => {
          button.classList.remove('loading', 'is-loading');
          button.textContent = 'Added';
          publishCartUpdate(source, data, variantId);
          resetButton(button, originalText, 1200);
          return data;
        });
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        button.classList.remove('loading', 'is-loading');
        // A genuine failure means the item is NOT in the cart -- restore the
        // button instead of claiming "In cart".
        button.textContent = originalText;
        button.disabled = false;
        throw error;
      });
  };

  function fetchCartAddVariant(variantId, cartEl, quantity, properties) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(Math.max(1, parseInt(quantity, 10) || 1)));
    if (properties && typeof properties === 'object') {
      Object.keys(properties).forEach((key) => {
        if (properties[key] != null && properties[key] !== '') {
          formData.append('properties[' + key + ']', String(properties[key]));
        }
      });
    }
    appendSections(formData, cartEl);
    return fetchCartAdd(formData);
  }

  window.vrvCartAddHelpers = {
    isMaxQuantityResponse,
    describeAddFailure,
    buildStockLimitMessage,
    showCartMessage,
    isCartAddError,
    isCartAddSuccess,
    fetchCartAdd,
    fetchCartAddItems,
    fetchCartAddVariant,
    syncCartUi,
    publishCartUpdate,
    getCartElement,
    appendSections,
    getCartAddSectionIds,
  };
})();
